import type { INotificationPreferencesApiPort } from '@personnel/application/ports/INotificationPreferencesApiPort';
import type {
  NotificationPreferenceConfigDto,
  NotificationPreferencesConfigDto,
} from '@personnel/application/dtos/NotificationPreferencesDto';
import { NOTIFICATION_CHANNEL_ORDER, NotificationGroups } from '@personnel/domain/types';
import { errorAttributes, perLog } from '@personnel/application/observability';

const SUPPORTED_GROUPS: ReadonlyArray<string> = Object.values(NotificationGroups);

/** Keeps only the channels the screen has a column for, in the order it paints them. */
const supportedChannels = (channels: ReadonlyArray<string>): NotificationPreferenceConfigDto['availableChannels'] =>
  NOTIFICATION_CHANNEL_ORDER.filter((channel) => channels.includes(channel));

/**
 * Use case for the notification preferences configuration.
 *
 * A group the application has no wording for is dropped here rather than in the screen, so that
 * neither the rows nor the body of the next write can ever mention it. Dropping it is what keeps a
 * group added by the backend from being switched off by a save that never showed it.
 *
 * @param {Object} props - Use case dependencies
 * @param {INotificationPreferencesApiPort} props.apiPort - Port for API operations
 */
export const getNotificationPreferencesConfigUseCase = (props: { apiPort: INotificationPreferencesApiPort }) => {
  const { apiPort } = props;

  const execute = async (): Promise<NotificationPreferencesConfigDto> => {
    try {
      const config = await apiPort.getConfig();
      const supported = config.filter((entry) => SUPPORTED_GROUPS.includes(entry.notificationGroup));
      const unsupported = config.filter((entry) => !SUPPORTED_GROUPS.includes(entry.notificationGroup));

      perLog.info('notificationPreferencesConfigLoaded', {
        'per.groupCount': supported.length,
        'per.unsupportedGroups': unsupported.map((entry) => entry.notificationGroup).join(','),
      });

      return supported.map((entry) => ({
        notificationGroup: entry.notificationGroup,
        availableChannels: supportedChannels(entry.availableChannels),
        mandatoryChannels: supportedChannels(entry.mandatoryChannels),
      }));
    } catch (error) {
      perLog.error('notificationPreferencesConfigLoaded', errorAttributes(error));
      throw error;
    }
  };

  return { execute };
};
